/**
 * useAboutSections
 *
 * Returns the static four-section About Expause catalogue rendered by
 * `AboutPage`. Web replacement for Flutter's `AboutUtils.getItems()`
 * (`lib/presentation/utils/about_the_app_utils.dart:2-41`) and preserves
 * Flutter's declared *return* order exactly — exclusive content → LIVE
 * streaming → top monetization → privacy (`about_the_app_utils.dart:40`,
 * note monetization is returned before privacy despite being constructed
 * after it).
 *
 * A pure l10n reader memoized on the `about` slice: the content is a static,
 * hard-coded local catalogue with no business logic and no use case, so there
 * is no unit test — the same shape as `useSupportItems` /
 * `useLegalDocumentsItems`. Sections are display-only, so the hook does not
 * navigate.
 */

import { useMemo } from 'react';
import type { AboutSectionEntity } from '@/domain/entities/about/aboutSectionEntity';
import { useLocale } from '@/presentation/contexts/LocaleContext';

export const useAboutSections = (): AboutSectionEntity[] => {
    const { l10n } = useLocale();
    const sections = l10n.about.sections;

    return useMemo(() => [
        {
            title: sections.exclusiveContent.title,
            items: sections.exclusiveContent.items,
        },
        {
            title: sections.liveStreaming.title,
            items: sections.liveStreaming.items,
        },
        {
            title: sections.topMonetization.title,
            items: sections.topMonetization.items,
        },
        {
            title: sections.privacy.title,
            items: sections.privacy.items,
        },
    ], [sections]);
};
